import StarRating from "components/StarRating";
import { useUrlQuery } from "hooks/useUrlQuery";
import { useHistory, useLocation } from "react-router-dom";
import useCurrencyStore from "store/currencyStore";
import { activeCurrencies, getActiveCurrencyCodeLabel, getActiveCurrencySymbol } from "utils";
import { IoPhonePortraitOutline } from "react-icons/io5";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { HiHeart, HiOutlineHeart } from "react-icons/hi";
import useIsInWishlist from "api/wishlistResource/useIsInWishlist";
import useAddToWishlist from "api/wishlistResource/useAddToWishlist";
import useRemoveFromWishlist from "api/wishlistResource/useRemoveFromWishlist";
import BookFormatLabel from "./BookFormatLabel";

const BookListItem = ({
	bookId,
	title,
	author,
	coverUrl,
	rating,
	price,
	zwl_price,
	sellable,
	isFree,
	format,
	showPrice = true,
	onClick,
	showReadOnAppBadge = false,
	showWishlist = false,
	isInWishlist: isInWishlistProp,
	hasPreview = false,
}) => {
	const history = useHistory();
	const location = useLocation();
	const query = useUrlQuery();
	const { currency } = useCurrencyStore();

	const { data: isInWishlistFromQuery } = useIsInWishlist(
		isInWishlistProp !== undefined ? null : showWishlist && bookId ? bookId : null,
	);
	const isInWishlist = isInWishlistProp ?? isInWishlistFromQuery ?? false;
	const { mutateAsync: addToWishlist, isLoading: isAddingToWishlist } = useAddToWishlist();
	const { mutateAsync: removeFromWishlist, isLoading: isRemovingFromWishlist } =
		useRemoveFromWishlist();

	const handleWishlistToggle = (e) => {
		e?.stopPropagation?.();
		if (!bookId) return;
		if (isInWishlist) {
			removeFromWishlist(bookId);
		} else {
			addToWishlist(bookId);
		}
	};

	const shouldShowPrice = () => {
		return price > 0 && !Boolean(isFree) && Boolean(sellable) && Boolean(showPrice);
	};

	const handleOpenBook = () => {
		// If custom onClick handler is provided, use it
		if (onClick) {
			return onClick();
		}

		let returnUrl = location.pathname;
		const queryString = query.toString().replace(/&/g, "lgAnd").replace(/-/g, "lgHyphen");
		if (query.toString().length > 0) {
			returnUrl = `${returnUrl}lgQuestionMark${queryString}`;
		}
		return history.push(`/book/${bookId}?returnUrl=${returnUrl}`);
	};

	const priceDisplay = shouldShowPrice()
		? `${getActiveCurrencySymbol(currency)}${currency === activeCurrencies.USD.code ? price?.toFixed(2) : zwl_price?.toFixed(2)}`
		: null;

	// const getFormatDisplay = () => {
	// 	if (!format) return "—";
	// 	if (format === BookFormat.Both) return "Both";
	// 	return format?.charAt(0).toUpperCase() + format?.slice(1) || "—";
	// };

	const hasBadges =
		Boolean(isFree) ||
		showReadOnAppBadge ||
		hasPreview ||
		(!Boolean(isFree) && showPrice) ||
		Boolean(format);

	return (
		<div
			onClick={handleOpenBook}
			className="group cursor-pointer bg-gradient-to-b from-gray-100 to-white border border-gray-200  rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 flex flex-col p-4 h-full min-w-0"
		>
			{/* Cover section with wishlist CTA */}
			<div className="relative flex flex-col items-center">
				<div
					className="bg-no-repeat bg-center rounded-lg shadow-md flex-shrink-0"
					style={{
						backgroundImage: coverUrl ? `url(${coverUrl})` : undefined,
						backgroundSize: "cover",
						width: 150,
						height: 230,
					}}
				>
					{!coverUrl && (
						<div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
							<svg
								className="w-12 h-12 text-gray-400"
								fill="currentColor"
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z" />
							</svg>
						</div>
					)}
				</div>

				{/* Wishlist icon: always visible on mobile, hover-revealed on desktop */}
				{showWishlist && bookId && (
					<button
						type="button"
						onClick={handleWishlistToggle}
						disabled={isAddingToWishlist || isRemovingFromWishlist}
						title={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
						className="absolute top-2 right-2 p-2 rounded-full bg-white backdrop-blur-sm shadow-md hover:bg-white focus:outline-none focus:ring-2 focus:ring-akgreen focus:ring-offset-2 disabled:opacity-50 transition-opacity opacity-100 md:opacity-0 md:group-hover:opacity-100 z-10"
						aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
					>
						{isAddingToWishlist || isRemovingFromWishlist ? (
							<AiOutlineLoading3Quarters className="w-5 h-5 text-akgreener animate-spin" />
						) : isInWishlist ? (
							<HiHeart className="w-5 h-5 text-red-500" />
						) : (
							<HiOutlineHeart className="w-5 h-5 text-akgreener" />
						)}
					</button>
				)}
			</div>

			{/* Pill-shaped status badges */}
			{hasBadges && (
				<div className="flex flex-wrap gap-1.5 mt-3 justify-center min-h-[24px]">
					{Boolean(isFree) && (
						<span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700">
							<span className="w-1.5 h-1.5 rounded-full bg-green-500" aria-hidden />
							Free
						</span>
					)}
					{showReadOnAppBadge && (
						<span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium bg-blue-50 text-blue-700">
							<IoPhonePortraitOutline size={12} />
							Read on App
						</span>
					)}
					{hasPreview && (
						<span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700">
							<span className="w-1.5 h-1.5 rounded-full bg-green-500" aria-hidden />
							Preview
						</span>
					)}
					{!Boolean(isFree) && showPrice && (
						<span className="rounded-full px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-700">
							{getActiveCurrencyCodeLabel(currency)}
						</span>
					)}
					{format && <BookFormatLabel format={format} className="inline-flex" />}
				</div>
			)}

			{/* Content hierarchy */}
			<div className="flex flex-col flex-1 mt-3 min-w-0">
				<h3 className="font-bold text-sm line-clamp-2 text-gray-800" title={title}>
					{title}
				</h3>
				<p className="text-sm text-gray-600 truncate mt-0.5" title={author}>
					{author}
				</p>
				<div className="mt-2">
					<StarRating number={rating} />
				</div>
			</div>

			{/* Metrics section */}
			<div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 mx-auto">
				<div>
					<p className="text-xs text-gray-500">Price</p>
					<p className="font-semibold text-gray-900 text-sm truncate">
						{priceDisplay ?? (Boolean(isFree) ? "Free" : "—")}
					</p>
				</div>
				<div>
					<p className="text-xs text-gray-500">Rating</p>
					<p className="font-semibold text-gray-900 text-sm">
						{rating != null ? `${Number(rating).toFixed(1)}` : "—"}
					</p>
				</div>
				{/* <div className="min-w-0">
					<p className="text-xs text-gray-500">Format</p>
					<p className="font-semibold text-gray-900 text-sm truncate">{getFormatDisplay()}</p>
				</div> */}
			</div>
		</div>
	);
};

export default BookListItem;
